import React, { useState, useRef } from 'react';
import api from '../api/axiosConfig';
import { useNavigate } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import { useModal } from '../contexts/ModalContext';

const reportTypes = ['LEAK', 'DAMAGE', 'ELECTRICAL', 'CLEANING', 'OTHER'];

function ReportForm() {
  const [type, setType] = useState(reportTypes[0]);
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 이 화면에서 올린 사진을 하나로 묶는 값. 제출하지 않고 나가면 청소 잡이
  // 걷어간다.
  const sessionIdRef = useRef(
    (crypto.randomUUID && crypto.randomUUID()) ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
  const navigate = useNavigate();
  const { showAlert } = useModal();

  const cameraInputRef = useRef(null);

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    setFiles(prevFiles => [...prevFiles, ...newFiles]);
    event.target.value = null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const uploadedPhotoUrls = [];
      for (const file of files) {
        const options = {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };

        const compressedFile = await imageCompression(file, options);

        const formData = new FormData();
        formData.append('file', compressedFile, compressedFile.name);
        formData.append('sessionId', sessionIdRef.current);

        const uploadResponse = await api.post('/reports/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        uploadedPhotoUrls.push(uploadResponse.data.url);
      }

      const reportData = {
        sessionId: sessionIdRef.current,
        type,
        location,
        description,
        photoUrls: uploadedPhotoUrls,
      };

      await api.post('/reports', reportData);
      navigate('/');

    } catch (error) {
      console.error('Error creating report:', error);
      showAlert('오류', '보고서 생성에 실패했습니다.', 'danger');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="card p-4">
        <h2 className="mb-4">신규 보고서 작성</h2>
        <form onSubmit={handleSubmit}>
          {/* Form fields for type, location, description */}
          <div className="mb-3">
            <label htmlFor="type" className="form-label">유형</label>
            <select id="type" className="form-select" value={type} onChange={e => setType(e.target.value)}>
              {reportTypes.map(reportType => (
                <option key={reportType} value={reportType}>{reportType}</option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label htmlFor="location" className="form-label">위치</label>
            <input type="text" id="location" className="form-control" value={location} onChange={e => setLocation(e.target.value)} required />
          </div>
          <div className="mb-3">
            <label htmlFor="description" className="form-label">상세 설명</label>
            <textarea id="description" className="form-control" rows="4" value={description} onChange={e => setDescription(e.target.value)} required></textarea>
          </div>

          {/* Hidden file inputs */}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={cameraInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          {/* Photo upload buttons */}
          <div className="mb-3">
            <label className="form-label">사진 첨부</label>
            <div className="d-grid gap-2 d-sm-flex">
              <button type="button" className="btn btn-outline-secondary" onClick={() => cameraInputRef.current.click()}>
                카메라로 촬영
              </button>
            </div>
          </div>

          {/* Display selected files */}
          {files.length > 0 && (
            <div className="mb-3">
              <h6>선택된 파일: {files.length}개</h6>
              <ul className="list-group">
                {files.map((file, index) => (
                  <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                    {file.name}
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Remove"
                      onClick={() => setFiles(files.filter((_, i) => i !== index))}
                    ></button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button type="submit" className="btn btn-primary w-100" disabled={isSubmitting}>
            {isSubmitting ? '제출 중...' : '제출'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ReportForm;
